import { create } from 'zustand';
import type { UserRole } from '../utils/auth';
import { parseAuthTokenForPermissions } from '../utils/auth';
import { deleteStoredInfo, storeCredentials } from '../utils/storage';

/**
 * Auth modal specific state
 */
type AuthModalState = {
  /** Current step of auth modal */
  currentStep?: 'login' | 'signup' | 'confirm' | 'login-long';
  /** Previous step for smart back button navigation */
  previousStep?: 'login' | 'signup' | 'confirm' | 'login-long';
  /** Email to pre-populate in auth modal (e.g., from ticket purchase flow) */
  email?: string;
  /** Whether verification code email has been sent (prevents duplicate sends) */
  codeSent?: boolean;
};

/**
 * Shared modal flow control
 * Single AuthFlowModal conditionally renders auth or onboarding content
 */
type ModalFlowState = {
  /** Whether the auth flow modal is currently visible */
  isVisible: boolean;
  /** Whether the user can dismiss the modal without completing the flow */
  isDismissible?: boolean;
  /** Callback to execute after entire auth+onboarding flow completes */
  onFlowComplete?: () => void;
  /** Callback to execute when flow is dismissed without completion */
  onFlowDismiss?: () => void;
};

/**
 * Core authentication state and user session data
 * Persisted to AsyncStorage and rehydrated on app launch
 */
type AuthState = {
  /** Whether the user has a valid authentication session */
  isAuthenticated?: boolean;
  /** Whether the app has completed the initial auth check (prevents showing wrong UI during hydration) */
  hasCheckedAuth?: boolean;
  /** Whether the user has completed the onboarding flow */
  isOnboarded?: boolean;
  /** Whether the app has checked the user's onboarding status */
  hasCheckedOnboarding?: boolean;
  /** Whether the user has admin privileges across the platform */
  isAdmin?: boolean;
  /** Community IDs where the user has owner-level permissions */
  ownedCommunityIds?: string[];
  /** Community IDs where the user has editor-level permissions */
  editorCommunityIds?: string[];
  /** Community IDs where the user has check-in permissions (for event management) */
  checkInCommunityIds?: string[];
  /** The user's role level (parsed from auth token) */
  currentUserRole?: UserRole;
  /** Whether the user has accepted the terms and conditions */
  hasAcceptedTerms?: boolean;
  /** Shared modal flow control */
  modalFlow: ModalFlowState;
  /** Auth modal specific state */
  authModal: AuthModalState;
  /** Force login state from /login route */
  forceLogin?: ForceLoginState;
} & AuthData;

/**
 * Force login state from /login route
 */
export type ForceLoginState = {
  /** Email to pre-populate and require login with */
  email?: string;
};

/**
 * Authentication token data
 * Stored in AsyncStorage and included in API request headers
 */
export type AuthData = {
  /** JWT access token for API authentication (short-lived) */
  authToken?: string;
  /** JWT refresh token used to obtain new access tokens (long-lived) */
  refreshToken?: string;
  /** The authenticated user's ID */
  currentUserId?: string;
};

/**
 * Actions for managing authentication state
 */
type AuthActions = {
  /** Updates authentication status and marks auth as checked */
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  /** Updates onboarding status and marks onboarding as checked */
  setIsOnboarded: (isOnboarded: boolean) => void;
  /** Updates auth tokens and user ID, parses token for user permissions, and persists to storage */
  setAuthData: (authData: AuthData) => Promise<void>;
  /** Opens the auth flow modal with optional flow config and auth state */
  openAuthFlowModal: (
    flowState?: Partial<ModalFlowState>,
    authState?: AuthModalState,
  ) => void;
  /** Updates auth modal state */
  updateAuthModal: (partial: Partial<AuthModalState>) => void;
  /** Completes the entire auth/onboarding flow - calls onFlowComplete and resets */
  completeModalFlow: () => void;
  /** Dismisses the flow without completion - calls onFlowDismiss and resets */
  dismissModalFlow: () => void;
  /** Sets force login state from /login route */
  setForceLogin: (forceLogin?: ForceLoginState) => void;
  /** Clears all auth state, resets to initial values, and removes persisted data from storage */
  clearAuth: () => void;
};

type AuthStore = AuthState & AuthActions;

const initialState = {
  hasCheckedAuth: false,
  hasCheckedOnboarding: false,
  isAuthenticated: false,
  isOnboarded: false,
  authToken: undefined,
  refreshToken: undefined,
  currentUserId: undefined,
  isAdmin: false,
  ownedCommunityIds: [],
  editorCommunityIds: [],
  checkInCommunityIds: [],
  currentUserRole: undefined,
  hasAcceptedTerms: false,
  modalFlow: {
    isVisible: false,
  },
  authModal: {
    currentStep: 'login',
  },
} satisfies AuthState;

export const useAuthStore = create<AuthStore>((set) => ({
  ...initialState,

  setIsAuthenticated: (isAuthenticated?: boolean) =>
    set({ isAuthenticated, hasCheckedAuth: true }),

  setIsOnboarded: (isOnboarded?: boolean) =>
    set({ isOnboarded, hasCheckedOnboarding: true }),

  setAuthData: async (authData: AuthData) => {
    const { authToken, refreshToken, currentUserId } = authData;
    set({
      authToken,
      refreshToken,
      currentUserId,
      isAuthenticated: Boolean(authToken),
      hasCheckedAuth: true,
    });
    if (authToken) set(parseAuthTokenForPermissions(authToken));
    await storeCredentials(authData);
  },

  openAuthFlowModal: (flowState = {}, authState = {}) => {
    set((state) => ({
      modalFlow: {
        ...state.modalFlow,
        isVisible: true,
        ...flowState,
      },
      authModal: {
        currentStep: 'login', // Default to login if not specified
        ...authState,
      },
    }));
  },

  updateAuthModal: (partial: Partial<AuthModalState>) => {
    set((state) => ({
      authModal: { ...state.authModal, ...partial },
    }));
  },

  completeModalFlow: () => {
    set(({ modalFlow }) => {
      // Call onFlowComplete callback if it exists before resetting state
      modalFlow.onFlowComplete?.();
      return {
        modalFlow: initialState.modalFlow,
        authModal: initialState.authModal,
      };
    });
  },

  dismissModalFlow: () =>
    set(({ modalFlow }) => {
      // Call onFlowDismiss callback if it exists before resetting state
      modalFlow.onFlowDismiss?.();
      return {
        modalFlow: initialState.modalFlow,
        authModal: initialState.authModal,
      };
    }),

  setForceLogin: (forceLogin?: ForceLoginState) => {
    set({ forceLogin });
  },

  clearAuth: () => {
    set((state) => ({
      ...initialState,
      hasCheckedAuth: true,
      modalFlow: state.modalFlow,
      authModal: state.authModal,
    }));
    void deleteStoredInfo();
  },
}));
